<template>
  <b-overlay
    :show="loading"
    rounded="sm"
    variant="primary"
    :opacity="0.2"
    style="min-height: 200px"
  >
    <b-card
      no-body
      class="border-0 mb-4 mt-3"
      v-show="professionsList.length">
      <div style="margin-bottom: 32px" v-if="isEnableCommonNames">
        <div class="mb-1">
          <filter-tag
            class="cluster-filter-tag border rounded"
            v-if="showClusterTag"
            :title="clusterTagTitle"
            :count="clusterTagCount"
            @click="startEditCluster"
            @delete="clearCluster"
          />
          <b-dropdown
            v-else
            variant="outline-secondary"
            class="w-100"
            menu-class="w-100"
            toggle-class="w-100 text-left form-control form-control-lg ai-custom-select"
            :text="clusterDropdownText"
            no-caret
          >
            <template #button-content>
              <div class="d-flex align-items-center justify-content-between w-100">
                <span class="text-truncate">{{ clusterDropdownText }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="m6 9 6 6 6-6"></path>
                </svg>
              </div>
            </template>
            <b-dropdown-item-button
              class="ai-custom-select-border"
              button-class="d-flex align-items-center justify-content-between w-100 py-2"
              v-for="c in clusterListWithCount"
              :key="c.id"
              @click="onClusterSelectChangeWithClose(c.id)"
            >
              {{ c.name }} <span class="">{{ toLocaleLangString(c.count) }} jobs</span>
            </b-dropdown-item-button>
          </b-dropdown>
        </div>

        <div class="position-relative mt-2 mb-2">
          <b-icon icon="search" class="position-absolute text-black-50" style="left: 15px; top: 50%; transform: translateY(-50%);" />
          <b-form-input
            v-model="professionSearchText"
            size="lg"
            class="pl-5 ai-custom-select"
            :placeholder="translations[15934]"
            @keydown.enter="onSearchEnter"
          />
          <div v-if="showAiSearchHint" class="ai-search-hint-badge">
            {{translations[15944]}}
          </div>
        </div>

        <b-card
          no-body
          class="bg-white ai-custom-box"
        >
          <b-card-body class="px-3 py-3">
            <div class="text-dark mb-2 font-weight-bold" style="font-size: 15px;">
              {{ translations[15938] }}
            </div>

            <div class="d-flex flex-column" style="row-gap: 8px;">
              <label class="d-flex align-items-center m-0" style="column-gap: 12px; cursor: pointer;">
                <input
                  v-model="qLevel1"
                  type="checkbox"
                  class="ai-qlevel-checkbox"
                  style="accent-color: #e5e7eb;"
                />
                <div class="d-flex align-items-center w-100" style="column-gap: 12px;">
                  <qualification-level-bars :level="1" />
                  <span class="text-dark" style="font-size: 14px;">{{ translations[15935] }}</span>
                </div>
              </label>

              <label class="d-flex align-items-center m-0" style="column-gap: 12px; cursor: pointer;">
                <input
                  v-model="qLevel2"
                  type="checkbox"
                  class="ai-qlevel-checkbox"
                  style="accent-color: #9ca3af;"
                />
                <div class="d-flex align-items-center w-100" style="column-gap: 12px;">
                  <qualification-level-bars :level="2" />
                  <span class="text-dark" style="font-size: 14px;">{{ translations[15936] }}</span>
                </div>
              </label>

              <label class="d-flex align-items-center m-0" style="column-gap: 12px; cursor: pointer;">
                <input
                  v-model="qLevel3"
                  type="checkbox"
                  class="ai-qlevel-checkbox"
                  style="accent-color: #4b5563;"
                />
                <div class="d-flex align-items-center w-100" style="column-gap: 12px;">
                  <qualification-level-bars :level="3" />
                  <span class="text-dark" style="font-size: 14px;">{{ translations[15937] }}</span>
                </div>
              </label>
            </div>
          </b-card-body>
        </b-card>
      </div>
      <b-card-body class="px-2 py-0">
        <h4 class="mb-3 h-custom">
          {{ jobsTitle }}
        </h4>

      </b-card-body>
      <professions-list
        :is-auth="isAuth"
        :language-sid="languageSid"
        :items="professionsListFiltered"
        :show-rank-bars="isEnableCommonNames"
      />
    </b-card>
  </b-overlay>
</template>

<script lang="ts">
import { Component, Prop, Watch } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { ProfessionModule } from '@/store/modules/profession';
import ProfessionsList from '@/components/Home/ProfessionsList.vue';
import FilterTag from '@/components/FilterTag.vue';
import QualificationLevelBars from '@/components/Home/QualificationLevelBars.vue';
import { UserModule } from '@/store/modules/user';
import { ClustersModule } from '@/store/modules/clusters';
import { mixins } from 'vue-class-component';
import LocaleStringMixin from '@/mixins/LocaleStringMixin';
import ProfessionsClusterSelectMixin from '@/mixins/ProfessionsClusterSelectMixin';
import { extractJobGroupFilters } from '@/api/AiTool';
import type { IAiExtractJobGroupFiltersProfession } from '@/types/aiTool';

@Component({
  components: {
    ProfessionsList,
    FilterTag,
    QualificationLevelBars,
  },
  mixins: [ProfessionsClusterSelectMixin],
})
export default class ProfessionsComponent extends mixins(LocaleStringMixin) {
  @Prop({ default: false }) private isCluster!: boolean;

  get translations() {
    return AppModule.translation;
  }

  loading = false;

  isClusterSelectEditing = false;

  professionSearchText = '';

  aiSearchResults: IAiExtractJobGroupFiltersProfession[] = [];

  isAiSearchActive = false;

  qLevel1 = true;

  qLevel2 = true;

  qLevel3 = true;

  get professions() {
    return ProfessionModule.professions;
  }

  get lang() {
    return AppModule.language;
  }

  get selectProfession() {
    return ProfessionModule.searchJobProfession;
  }

  get selectLocation() {
    return ProfessionModule.searchJobLocation;
  }

  get professionTitle() {
    return ProfessionModule.professionTitle || ClustersModule.professionTitle;
  }

  get jobsTitle() {
    const count = this.filteredJobsCount;
    if (this.selectProfession || this.selectLocation) {
      return `${this.toLocaleLangString(count || 0)} ${count === 1 ? this.translations[13772] : this.translations[13773]} ${this.selectProfession?.name || ''}${
        (this.selectProfession?.name && this.selectLocation?.name) ? ',' : ''
      } ${this.selectLocation?.name || ''}`;
    }
    return `${this.toLocaleLangString(count || 0)} ${count === 1 ? this.translations[13785] : this.translations[13774]}`; // ${this.isCluster ? `: ${this.professionTitle}` : ''}
  }

  get professionsList() {
    return this.professions.professions?.flat().sort((a: any, b: any) => b.amt - a.amt) || [];
  }

  get professionsListFiltered() {
    const enabledRanks: number[] = [];
    if (this.qLevel1) enabledRanks.push(1);
    if (this.qLevel2) enabledRanks.push(2);
    if (this.qLevel3) enabledRanks.push(3);

    if (this.isAiSearchActive && this.aiSearchResults.length > 0) {
      const professionsMap = new Map(this.professionsList.map((p: any) => [p.id, p]));
      let list = this.aiSearchResults
        .map((aiProf) => professionsMap.get(aiProf.code))
        .filter((p) => p != null);

      if (enabledRanks.length) {
        list = list.filter((job: any) => enabledRanks.includes(Number(job?.rank)));
      } else {
        list = [];
      }

      return list;
    }

    let list = this.professionsList;
    if (enabledRanks.length) {
      list = list.filter((job: any) => enabledRanks.includes(Number(job?.rank)));
    } else {
      list = [];
    }

    const q = (this.professionSearchText || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter((job: any) => String(job?.tit || '').toLowerCase().includes(q));
  }

  get showAiSearchHint() {
    return !!this.professionSearchText?.trim() && this.professionsListFiltered.length === 0;
  }

  get allCount(): number {
    return Number(AppModule.setting?.all_count || 0);
  }

  get clustersCount(): any {
    const id = ClustersModule.jobClusterParams.cluster_id;
    const clusters = AppModule.jobCountPortal?.clusters || [];
    return clusters.find((x: any) => x.id === Number(id)) || null;
  }

  get selectedCluster() {
    const id = ClustersModule.jobClusterParams.cluster_id;
    if (!id) return null;
    const nid = Number(id);
    return (this as any).clusterListWithCount?.find((c: any) => c.id === nid) || null;
  }

  get showClusterTag() {
    return !!this.selectedCluster && !this.isClusterSelectEditing;
  }

  get clusterTagTitle() {
    return this.selectedCluster?.name || '';
  }

  get clusterTagCount() {
    return this.selectedCluster?.count || 0;
  }

  get clusterDropdownText() {
    if (this.selectedCluster) {
      return `${this.selectedCluster.name} (${this.toLocaleLangString(this.selectedCluster.count || 0)})`;
    }
    return this.translations[15933];
  }

  get clusterDropdownAllText() {
    return `${this.toLocaleLangString(this.allCount || 0)} ${this.allCount === 1 ? this.translations[13785] : this.translations[13774]}`;
  }

  startEditCluster() {
    this.isClusterSelectEditing = true;
  }

  clearCluster() {
    (this as any).onClusterSelectChange(0);
    this.isClusterSelectEditing = false;
  }

  onClusterSelectChangeWithClose(value: number | string | null) {
    (this as any).onClusterSelectChange(value);
    this.isClusterSelectEditing = false;
  }

  get jobsCount() {
    if (this.isCluster) {
      return this.clustersCount?.count ?? 0;
    }
    return this.allCount;
  }

  get filteredJobsCount() {
    return this.professionsListFiltered.reduce((sum: number, job: any) => sum + Number(job?.amt || 0), 0);
  }

  get languageSid() {
    return AppModule.languageSid;
  }

  get sid() {
    return AppModule.sid;
  }

  get isEnableCommonNames() {
    return Number(AppModule.setting?.enable_common_names) === 1;
  }

  get isAuth() {
    return UserModule.isUserAuth;
  }

  async loadProfessionItems() {
    this.loading = true;
    const clusterId = ClustersModule.jobClusterParams.cluster_id;
    const params = {
      sid: this.languageSid,
      lang: this.lang,
      ...(clusterId ? { cluster_id: clusterId } : {}),
      ...(this.selectProfession && { profession_id: this.selectProfession.id }),
    };
    await ProfessionModule.GetProfession(params);
    this.loading = false;
  }

  async onSearchEnter() {
    const searchText = (this.professionSearchText || '').trim();
    if (!searchText) {
      this.isAiSearchActive = false;
      this.aiSearchResults = [];
      return;
    }

    try {
      this.loading = true;
      const response = await extractJobGroupFilters({
        text: searchText,
        sid: this.sid,
      });
      this.aiSearchResults = response.filters.professions || [];
      this.isAiSearchActive = true;
    } catch (error) {
      console.error('AI search error:', error);
      this.aiSearchResults = [];
      this.isAiSearchActive = false;
    } finally {
      this.loading = false;
    }
  }

  @Watch('professionSearchText')
  onSearchTextChange(newValue: string, oldValue: string) {
    const newLen = (newValue || '').length;
    const oldLen = (oldValue || '').length;
    if (newLen < oldLen && this.isAiSearchActive) {
      this.isAiSearchActive = false;
      this.aiSearchResults = [];
    }
  }

  created() {
    const selectedClusterId = ProfessionModule.jobFilter.cluster_id;
    const clusterParamId = ClustersModule.jobClusterParams.cluster_id;
    if (selectedClusterId && !clusterParamId) {
      ClustersModule.SetClusterParams({ obj: 'cluster_id', value: Number(selectedClusterId) });
      const selectedCluster = (AppModule.settingPortal?.clusters || []).find((x: any) => x.id === Number(selectedClusterId));
      ClustersModule.SetProfessionTitle(selectedCluster?.name || '');
    }
    this.loadProfessionItems();
  }
}
</script>

<style scoped>
/* Чекбокси Qualifikationsniveau як у макеті */
.ai-qlevel-checkbox {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  cursor: pointer;
}
.h-custom{
  color: #364153!important;
  font-size: 18px;
  font-family: Nunito, sans-serif; font-weight: 600;
}
.cluster-filter-tag {
  font-size: 15px!important;
  padding: 0.35rem 0.7rem!important;
  border: 2px solid #dee2e6 !important;
  background-color: #fff!important;
  border-radius: 20px!important;
  transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;
}

.cluster-filter-tag:hover, .cluster-filter-tag:active  {
  border: 2px solid #99a1af !important;
  background-color: #fbf9fa !important;

}

.ai-search-hint-badge {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  pointer-events: none;
  font-size: 12px;
  line-height: 1;
  color: #111827;
  background: #fde68a;
  border: 1px solid #f59e0b;
  border-radius: 999px;
  padding: 6px 10px;
  white-space: nowrap;
  animation: ai-hint-pulse 1.25s ease-in-out infinite;
}

@keyframes ai-hint-pulse {
  0% { opacity: 0.65; transform: translateY(-50%) scale(0.98); }
  50% { opacity: 1; transform: translateY(-50%) scale(1); }
  100% { opacity: 0.65; transform: translateY(-50%) scale(0.98); }
}
</style>
